/**
 * Recovery from a deployment while the app is open.
 *
 * The open tab keeps the previous `index.html`, whose lazily loaded chunks no
 * longer exist after a new build is deployed. Loading one fails, so the page
 * is reloaded once to pick up the new build. A short guard prevents a reload
 * loop when the chunk is missing for another reason (server down, bad build).
 */

const RELOAD_KEY = 'app.staleBuildReloadAt';
/** A second failure within this window is a real error, not a stale build. */
const RELOAD_GUARD_MS = 10_000;

/** Browser messages for a dynamic `import()` that could not be fetched. */
const CHUNK_ERROR_PATTERNS = [
	/Failed to fetch dynamically imported module/i, // Chromium
	/error loading dynamically imported module/i, // Firefox
	/Importing a module script failed/i, // Safari
	/Unable to preload CSS/i, // Vite CSS preload
];

export function isChunkLoadError(error: unknown): boolean {
	const message =
		error instanceof Error
			? error.message
			: typeof error === 'string'
				? error
				: '';
	return CHUNK_ERROR_PATTERNS.some((pattern) => pattern.test(message));
}

/**
 * Reload to load the current build. Returns `false` (and does nothing) when a
 * reload for the same reason just happened.
 */
export function reloadForNewBuild(now: number = Date.now()): boolean {
	try {
		const last = Number(sessionStorage.getItem(RELOAD_KEY) ?? 0);
		if (now - last < RELOAD_GUARD_MS) return false;
		sessionStorage.setItem(RELOAD_KEY, String(now));
	} catch {
		// Storage unavailable (private mode): reload anyway, without a guard.
	}
	window.location.reload();
	return true;
}

/** Vite emits `vite:preloadError` when a lazily loaded chunk fails to load. */
export function installStaleBuildRecovery(): void {
	window.addEventListener('vite:preloadError', (event) => {
		// Handled: stop the error from surfacing while the page reloads.
		if (reloadForNewBuild()) event.preventDefault();
	});
}
